import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import { ReactNode, useMemo, useState } from "react";
import {
  useLazyGetOrderBalanceQuery,
  useLazyGetOrderBalanceWorkbookQuery,
} from "store/queries/orderBalance";
import styled from "styled-components";
import {
  IOrderBalance,
  IOrderBalanceBoxRow,
  IOrderBalanceEvent,
  IOrderBalanceInvariant,
  IOrderBalanceParty,
  IOrderBalanceReportRow,
  IOrderBalanceTransferRow,
} from "types/OrderBalance";

import {
  BALANCE_BOX_HEADINGS,
  EventTableRow,
  balanceVerdict,
  buildEventRows,
  eventTypeText,
  feeRatesText,
  formatCents,
  formatDate,
  isReportedPending,
  partitionChecks,
  partyField,
  sumField,
} from "./helpers";

const StyledContainer = styled.div`
  background: var(--white-100, #fff);
  border: 1px solid var(--gray-200, #e5e7eb);
  border-radius: 8px;
  padding: 16px;
  /* Never widen the page: every table scrolls inside the card instead. */
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;

  .table-scroll {
    max-width: 100%;
    overflow-x: auto;
  }

  /* The wrapper scrolls; the table is as wide as its content and at least the wrapper. PrimeReact's
     \`scrollable\` prop is deliberately NOT used here — its flex-row layout plus a max-content
     min-width resolved to a 500,000px table (CLE-2849 QA rerun). */
  .table-scroll .p-datatable-table {
    table-layout: auto;
    width: max-content;
    min-width: 100%;
  }

  .table-scroll .p-datatable-thead > tr > th,
  .table-scroll .p-datatable-tbody > tr > td,
  .table-scroll .p-datatable-tfoot > tr > td {
    white-space: nowrap;
  }

  /* A visible rule opening each provider group / column family, as the workbook's blocks read. */
  .table-scroll .p-datatable-thead > tr > th.group-start,
  .table-scroll .p-datatable-tbody > tr > td.group-start,
  .table-scroll .p-datatable-tfoot > tr > td.group-start {
    border-left: 2px solid var(--gray-300, #d1d5db);
  }

  .not-applicable-toggle {
    color: var(--gray-500, #6b7280);
    font-size: 0.85rem;
  }

  .check-details {
    list-style: disc;
    margin: 4px 0 0;
    padding-left: 2.5rem;
  }

  .informational-row {
    color: var(--gray-500, #6b7280);
    font-style: italic;
  }

  .outstanding {
    color: var(--gray-600, #4b5563);
  }

  h4 {
    margin: 20px 0 8px;
  }

  .p-datatable {
    font-size: 0.85rem;
  }

  .balance-nonzero {
    background: #fde8e8;
    color: #9b1c1c;
    font-weight: 600;
  }

  .money {
    text-align: right;
    white-space: nowrap;
  }

  .reported-pending {
    color: var(--gray-500, #6b7280);
    font-style: italic;
  }
`;

const StatusIcon = styled.i<{ $status: string }>`
  margin-right: 8px;
  color: ${({ $status }) => {
    if ($status === "PASS") return "#057a55";
    if ($status === "FAIL") return "#c81e1e";
    return "#6b7280";
  }};
`;

const statusIcon = (status: string) => {
  if (status === "PASS") return "pi pi-check-circle";
  if (status === "FAIL") return "pi pi-times-circle";
  return "pi pi-minus-circle";
};

const moneyCell = (value: number | null | undefined, highlight = false) => (
  <span className={highlight && value ? "balance-nonzero" : undefined}>
    {formatCents(value)}
  </span>
);

// Cell templates live at module scope (react/no-unstable-nested-components).
const accountBody = (row: IOrderBalanceBoxRow) =>
  row.kind === "TOTAL" ? <strong>{row.name}</strong> : row.name;

const eventTypeBody = (row: EventTableRow) => (
  <span title={(row.notes as string) || undefined}>
    {eventTypeText(String(row.type), String(row.label ?? ""))}
  </span>
);

// A reported figure no weekly report covers yet renders as a muted "pending", never as red $0.00.
const boxCell = (
  balanceBox: IOrderBalance["balanceBox"],
  row: IOrderBalanceBoxRow,
  field: keyof IOrderBalanceBoxRow,
  highlight: boolean
) =>
  isReportedPending(balanceBox, row, field) ? (
    <span
      className="reported-pending"
      title="No weekly report covers this order yet"
    >
      pending
    </span>
  ) : (
    moneyCell(row[field] as number | null, highlight)
  );

const verifiedBody = (row: { verified: unknown; verificationNote?: string }) =>
  row.verified ? (
    <i
      className="pi pi-check"
      title={row.verificationNote || "Verified at the processor"}
    />
  ) : (
    <span title={row.verificationNote || "Not verified at the processor"}>
      DB only
    </span>
  );

type ReportTableRow = IOrderBalanceReportRow & { partyName: string };

const reportGroupHeader = (row: ReportTableRow) => (
  <strong>{row.partyName}</strong>
);

const reportIdBody = (row: ReportTableRow) => (
  <span>
    {row.reportId}
    {row.isHidden ? " (hidden)" : ""}
    {row.publishedAt ? "" : " (unpublished)"}
  </span>
);

// Wraps a table so a wide one scrolls inside the card rather than widening the page.
function TableScroll({
  children,
  testId,
}: {
  children: ReactNode;
  testId: string;
}) {
  return (
    <div className="table-scroll" data-testid={testId}>
      {children}
    </div>
  );
}

const eventRowClassName = (row: EventTableRow) =>
  row.informational ? "informational-row" : "";

function BalanceBoxTable({ ledger }: { ledger: IOrderBalance }) {
  const { balanceBox } = ledger;
  const rows = [...balanceBox.rows, balanceBox.total];
  const patientFee = balanceBox.total.clearPatientFeeInCents ?? 0;
  // One column per workbook heading; `groupStart` draws the separator that opens a column family.
  const boxColumn = (
    field: keyof IOrderBalanceBoxRow,
    highlight: boolean,
    groupStart = false
  ) => {
    const heading = BALANCE_BOX_HEADINGS[field];
    return (
      <Column
        key={field}
        header={heading?.header}
        headerTooltip={heading?.tooltip}
        headerClassName={groupStart ? "group-start" : undefined}
        bodyClassName={groupStart ? "money group-start" : "money"}
        body={(row: IOrderBalanceBoxRow) =>
          boxCell(balanceBox, row, field, highlight)
        }
      />
    );
  };
  const adjustments = balanceBox.adjustmentsInCents ?? 0;
  return (
    <>
      <TableScroll testId="scroll-balance-box">
        <DataTable value={rows} dataKey="partyId" size="small">
          <Column
            header="Account"
            headerTooltip="Each provider on the order, then Clear Health, then the total."
            body={accountBody}
          />
          {boxColumn("providerOwedInCents", false, true)}
          {boxColumn("providerPaidInCents", false)}
          {boxColumn("reportedPaidInCents", false)}
          {boxColumn("providerTotalInCents", false, true)}
          {boxColumn("reportedTotalInCents", false)}
          {boxColumn("cashBalanceInCents", true, true)}
          {boxColumn("reportedCashBalanceInCents", true)}
          {boxColumn("reportedTppBalanceInCents", true)}
          <Column
            header={BALANCE_BOX_HEADINGS.outstandingInCents?.header}
            headerTooltip={BALANCE_BOX_HEADINGS.outstandingInCents?.tooltip}
            headerClassName="group-start"
            bodyClassName="money outstanding group-start"
            body={(row: IOrderBalanceBoxRow) =>
              formatCents(row.outstandingInCents)
            }
          />
          {patientFee !== 0 && (
            <Column
              header={BALANCE_BOX_HEADINGS.clearPatientFeeInCents?.header}
              headerTooltip={
                BALANCE_BOX_HEADINGS.clearPatientFeeInCents?.tooltip
              }
              bodyClassName="money"
              body={(row: IOrderBalanceBoxRow) =>
                formatCents(row.clearPatientFeeInCents)
              }
            />
          )}
        </DataTable>
      </TableScroll>
      <div className="flex flex-wrap gap-4 mt-2">
        <span>
          Patient Paid:{" "}
          <strong>{formatCents(balanceBox.patientPaidInCents)}</strong>
        </span>
        <span data-testid="outstanding-total">
          Still to be collected:{" "}
          <strong className="outstanding">
            {formatCents(balanceBox.patientBalanceInCents)}
          </strong>{" "}
          of {formatCents(balanceBox.estimateInCents)} estimated
          {adjustments !== 0 &&
            ` (after ${formatCents(adjustments)} of balance adjustments)`}{" "}
          (not a balance)
        </span>
        {patientFee !== 0 && (
          <span data-testid="patient-fee-note">
            Clear also collected {formatCents(patientFee)} in patient fees, not
            shown in weekly reports (left out of Provider Total and Clear
            Health&apos;s Reported Cash Balance).
          </span>
        )}
        {!balanceBox.reportsCoverOrder && (
          <span className="reported-pending" data-testid="reported-pending">
            Reported figures pending — no weekly report covers this order yet.
          </span>
        )}
        {balanceBox.unallocatedInCents !== 0 && (
          <span>
            Unallocated on the platform:{" "}
            {moneyCell(balanceBox.unallocatedInCents, true)}
          </span>
        )}
      </div>
    </>
  );
}

function EventTable({
  events,
  providers,
  showVerification,
}: {
  events: IOrderBalanceEvent[];
  providers: IOrderBalanceParty[];
  showVerification: boolean;
}) {
  const rows = useMemo(
    () => buildEventRows(events, providers),
    [events, providers]
  );
  const fields: Array<"total" | "provider" | "fee"> = [
    "total",
    "provider",
    "fee",
  ];
  // The workbook's per-provider headings.
  const fieldHeaders = {
    total: "Total Patient Payment",
    provider: "Provider Split",
    fee: "Clear Fee",
  };
  // The first column of every provider group carries the separator, so each group reads as a block.
  const groupClass = (f: string) => (f === "total" ? "group-start" : undefined);

  const header = (
    <ColumnGroup>
      <Row>
        <Column header="Date" rowSpan={2} />
        <Column header="Type" rowSpan={2} />
        <Column
          header={showVerification ? "Patient" : "Order Total"}
          rowSpan={2}
        />
        <Column header="Provider Split Total" rowSpan={2} />
        <Column
          header="Clear Total"
          headerTooltip="Every Clear fee on the row plus cash Clear Health moved directly"
          rowSpan={2}
        />
        {providers.map((p) => (
          <Column
            key={p.id}
            header={p.name}
            colSpan={3}
            headerClassName="group-start"
          />
        ))}
        <Column
          header="Clear Health (direct)"
          rowSpan={2}
          headerClassName="group-start"
        />
        {showVerification && <Column header="Unallocated" rowSpan={2} />}
        {showVerification && <Column header="Verified" rowSpan={2} />}
      </Row>
      <Row>
        {providers.flatMap((p) =>
          fields.map((f) => (
            <Column
              key={`${p.id}-${f}`}
              header={fieldHeaders[f]}
              headerClassName={groupClass(f)}
            />
          ))
        )}
      </Row>
    </ColumnGroup>
  );

  const footer = (
    <ColumnGroup>
      <Row>
        <Column footer="Total" colSpan={2} />
        <Column footer={formatCents(sumField(rows, "patient"))} />
        <Column footer={formatCents(sumField(rows, "providerTotal"))} />
        <Column footer={formatCents(sumField(rows, "clearTotal"))} />
        {providers.flatMap((p) =>
          fields.map((f) => (
            <Column
              key={`${p.id}-${f}-total`}
              footer={formatCents(sumField(rows, partyField(p.id, f)))}
              footerClassName={groupClass(f)}
            />
          ))
        )}
        <Column
          footer={formatCents(sumField(rows, "clearDirect"))}
          footerClassName="group-start"
        />
        {showVerification && (
          <Column footer={formatCents(sumField(rows, "unallocated"))} />
        )}
        {showVerification && <Column footer="" />}
      </Row>
    </ColumnGroup>
  );

  return (
    <DataTable
      value={rows}
      dataKey="id"
      size="small"
      headerColumnGroup={header}
      footerColumnGroup={footer}
      emptyMessage="Nothing recorded."
      rowClassName={eventRowClassName}
    >
      <Column
        field="date"
        body={(row: EventTableRow) => formatDate(row.date as string)}
      />
      <Column field="type" body={eventTypeBody} />
      <Column
        field="patient"
        bodyClassName="money"
        body={(row: EventTableRow) => formatCents(row.patient as number)}
      />
      <Column
        field="providerTotal"
        bodyClassName="money"
        body={(row: EventTableRow) => formatCents(row.providerTotal as number)}
      />
      <Column
        field="clearTotal"
        bodyClassName="money"
        body={(row: EventTableRow) => formatCents(row.clearTotal as number)}
      />
      {providers.flatMap((p) =>
        fields.map((f) => (
          <Column
            key={`${p.id}-${f}-body`}
            field={partyField(p.id, f)}
            bodyClassName={f === "total" ? "money group-start" : "money"}
            body={(row: EventTableRow) =>
              formatCents(row[partyField(p.id, f)] as number | null)
            }
          />
        ))
      )}
      <Column
        field="clearDirect"
        bodyClassName="money group-start"
        body={(row: EventTableRow) =>
          row.clearDirect ? formatCents(row.clearDirect as number) : ""
        }
      />
      {showVerification && (
        <Column
          field="unallocated"
          bodyClassName="money"
          body={(row: EventTableRow) =>
            moneyCell(row.unallocated as number, true)
          }
        />
      )}
      {showVerification && <Column field="verified" body={verifiedBody} />}
    </DataTable>
  );
}

function ReportsTable({
  rows,
  partyName,
}: {
  rows: IOrderBalanceReportRow[];
  partyName: (id: string) => string;
}) {
  const tableRows: ReportTableRow[] = rows.map((row) => ({
    ...row,
    partyName: partyName(row.providerId),
  }));
  return (
    <TableScroll testId="scroll-reports">
      <DataTable
        value={tableRows}
        size="small"
        emptyMessage="No weekly report covers this order yet."
        rowGroupMode="subheader"
        groupRowsBy="providerId"
        rowGroupHeaderTemplate={reportGroupHeader}
      >
        <Column
          header="Report date"
          body={(r: IOrderBalanceReportRow) => formatDate(r.reportDate)}
        />
        <Column
          header="Amount Paid"
          bodyClassName="money"
          body={(r: IOrderBalanceReportRow) => formatCents(r.amountPaidInCents)}
        />
        <Column
          header="Clear Fee Withheld"
          bodyClassName="money"
          body={(r: IOrderBalanceReportRow) =>
            formatCents(r.clearFeeWithheldInCents)
          }
        />
        <Column
          header="Total Patient Payment"
          bodyClassName="money"
          body={(r: IOrderBalanceReportRow) =>
            formatCents(r.totalPatientPaymentInCents)
          }
        />
        <Column header="Date Paid" field="datePaid" />
        <Column header="Provider to provider" field="providerToProvider" />
        <Column header="Processor" field="processor" />
        <Column header="Notes" field="notes" />
        <Column header="Report" body={reportIdBody} />
      </DataTable>
    </TableScroll>
  );
}

function TransferRowsTable({
  rows,
  partyName,
}: {
  rows: IOrderBalanceTransferRow[];
  partyName: (id: string | null) => string;
}) {
  return (
    <TableScroll testId="scroll-processor-rows">
      <DataTable
        value={rows}
        dataKey="id"
        size="small"
        emptyMessage="No processor rows."
      >
        <Column
          header="Created"
          body={(r: IOrderBalanceTransferRow) => formatDate(r.createdAt)}
        />
        <Column
          header="Party"
          body={(r: IOrderBalanceTransferRow) => partyName(r.providerId)}
        />
        <Column header="Kind" field="kind" />
        <Column
          header="Amount (DB)"
          bodyClassName="money"
          body={(r: IOrderBalanceTransferRow) => formatCents(r.amountInCents)}
        />
        <Column
          header="Clear Fee (DB)"
          bodyClassName="money"
          body={(r: IOrderBalanceTransferRow) =>
            formatCents(r.clearFeeAmountInCents)
          }
        />
        <Column
          header="Patient Fee (DB)"
          bodyClassName="money"
          body={(r: IOrderBalanceTransferRow) =>
            r.clearPatientFeeAmountInCents
              ? formatCents(r.clearPatientFeeAmountInCents)
              : ""
          }
        />
        <Column header="Processor id" field="processorId" />
        <Column
          header="Processor"
          bodyClassName="money"
          body={(r: IOrderBalanceTransferRow) =>
            r.processorAmountInCents === null
              ? ""
              : `${formatCents(r.processorAmountInCents)}${
                  r.processorAmountReversedInCents
                    ? ` (reversed ${formatCents(
                        r.processorAmountReversedInCents
                      )})`
                    : ""
                }`
          }
        />
        <Column header="Verified" body={verifiedBody} />
        <Column header="Report" field="reportId" />
      </DataTable>
    </TableScroll>
  );
}

function InvariantList({
  invariants,
}: {
  invariants: IOrderBalanceInvariant[];
}) {
  const [showNotApplicable, setShowNotApplicable] = useState(false);
  const { applicable, notApplicable } = partitionChecks(invariants);
  return (
    <>
      <ul className="list-none p-0 m-0">
        {applicable.map((check) => (
          <li
            key={check.name}
            className="mb-2"
            data-testid={`invariant-${check.name}`}
          >
            <StatusIcon
              $status={check.status}
              className={statusIcon(check.status)}
              aria-label={check.status}
            />
            <strong>{check.title}</strong> — {check.status}: {check.message}
            {check.details.length > 0 && (
              <ul className="check-details">
                {check.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {notApplicable.length > 0 && (
        <div data-testid="not-applicable-checks">
          <button
            type="button"
            className="p-link not-applicable-toggle"
            onClick={() => setShowNotApplicable(!showNotApplicable)}
            aria-expanded={showNotApplicable}
          >
            <i
              className={
                showNotApplicable ? "pi pi-chevron-down" : "pi pi-chevron-right"
              }
            />{" "}
            {notApplicable.length} check{notApplicable.length === 1 ? "" : "s"}{" "}
            not applicable to this order
          </button>
          {showNotApplicable && (
            <ul className="check-details not-applicable-toggle">
              {notApplicable.map((check) => (
                <li key={check.name} data-testid={`invariant-${check.name}`}>
                  {check.title} — {check.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

/**
 * CLE-2849: SuperAdmin-only per-order balance ledger — Ryan's reconciliation workbook in the product.
 * Collapsed and idle until "Run balance check": the endpoint reads Stripe/Finix live, so it must
 * never fire on page open.
 */
export function OrderBalance({ orderId }: { orderId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [trigger, { data, isFetching, error }] = useLazyGetOrderBalanceQuery();
  const [fetchWorkbook, { isFetching: isDownloading }] =
    useLazyGetOrderBalanceWorkbookQuery();
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const ledger = data?.data;

  const providers = useMemo(
    () => (ledger?.parties ?? []).filter((p) => p.kind === "PROVIDER"),
    [ledger]
  );
  const partyName = (id: string | null) =>
    ledger?.parties.find((p) => p.id === id)?.name ??
    id ??
    "Clear Health / patient";

  const run = () => {
    setExpanded(true);
    setCopied(false);
    trigger(orderId);
  };

  const copyJson = () => {
    if (!ledger) return;
    navigator.clipboard
      ?.writeText(JSON.stringify(ledger, null, 2))
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const downloadExcel = async () => {
    setDownloadError(null);
    try {
      const { blob, fileName } = await fetchWorkbook(orderId).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setDownloadError(e?.data?.message ?? "The workbook could not be built.");
    }
  };

  const verdict = ledger ? balanceVerdict(ledger) : null;

  return (
    <StyledContainer data-testid="order-balance">
      <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
        <button
          type="button"
          className="p-link flex align-items-center gap-2"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          <i
            className={expanded ? "pi pi-chevron-down" : "pi pi-chevron-right"}
          />
          <h3 className="m-0">Order balance (SuperAdmin)</h3>
        </button>
        <div className="flex gap-2">
          {ledger && (
            <Button
              label={copied ? "Copied" : "Copy JSON"}
              icon="pi pi-copy"
              className="p-button-outlined p-button-sm"
              onClick={copyJson}
            />
          )}
          {ledger && (
            <Button
              label="Download Excel"
              icon="pi pi-file-excel"
              className="p-button-outlined p-button-sm"
              loading={isDownloading}
              onClick={downloadExcel}
            />
          )}
          <Button
            label={ledger ? "Re-run balance check" : "Run balance check"}
            icon="pi pi-calculator"
            className="p-button-sm"
            loading={isFetching}
            onClick={run}
          />
        </div>
      </div>

      {expanded && (
        <div className="mt-3">
          {!ledger && !isFetching && !error && (
            <p className="m-0">
              Compares what should have happened, what the processor did and
              what the weekly reports said. Reads Stripe/Finix live, so it only
              runs when you ask.
            </p>
          )}
          {error && (
            <p className="m-0 text-red-600">
              The balance check failed:{" "}
              {(error as any)?.data?.message ??
                (error as any)?.status ??
                "unknown error"}
            </p>
          )}
          {downloadError && (
            <p className="mt-0 text-red-600" data-testid="download-error">
              Download failed: {downloadError}
            </p>
          )}
          {ledger && (
            <>
              <p className="m-0">
                Order <code>{ledger.orderId}</code> · {feeRatesText(ledger)} ·{" "}
                {ledger.processors.join(" + ") || "no processor"} ·{" "}
                {ledger.clearKeepsFeeOnRefund
                  ? "Clear keeps its fee on refunds"
                  : "refunds return Clear's fee"}{" "}
                · run {new Date(ledger.generatedAt).toLocaleString("en-US")}
              </p>
              <p className="mt-1 mb-0" data-testid="order-balance-verdict">
                <strong style={{ color: verdict?.ok ? "#057a55" : "#c81e1e" }}>
                  {verdict?.text}
                </strong>
              </p>

              <h4>Summary</h4>
              <BalanceBoxTable ledger={ledger} />

              <h4>Order Overview — what was supposed to happen</h4>
              <TableScroll testId="scroll-order-overview">
                <EventTable
                  events={ledger.orderOverview}
                  providers={providers}
                  showVerification={false}
                />
              </TableScroll>

              <h4>
                Payment Processor Activity — what happened at the processor
              </h4>
              <TableScroll testId="scroll-payment-activity">
                <EventTable
                  events={ledger.paymentActivity}
                  providers={providers}
                  showVerification
                />
              </TableScroll>

              <h4>Reconciliation Reports — what the weekly reports said</h4>
              <ReportsTable
                rows={ledger.reconciliationReports.rows}
                partyName={partyName}
              />

              <h4>Checks</h4>
              <InvariantList invariants={ledger.invariants} />

              <h4>Processor rows</h4>
              {ledger.processorVerification.errors.length > 0 && (
                <ul className="mt-0">
                  {ledger.processorVerification.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
              <TransferRowsTable
                rows={ledger.transferRows}
                partyName={partyName}
              />
            </>
          )}
        </div>
      )}
    </StyledContainer>
  );
}

export default OrderBalance;
